import React, { useState } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Typography,
  Divider,
  ListItemIcon,
  ListItemText,
  Chip,
  Avatar,
  CircularProgress
} from '@mui/material';
import {
  KeyboardArrowDown as ArrowDownIcon,
  Business as BusinessIcon,
  Add as AddIcon,
  Settings as SettingsIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { useOrganization } from '../../contexts/OrganizationContext';
import CreateOrganizationForm from './CreateOrganizationForm';

const OrganizationSwitcher = ({ onSettingsClick }) => {
  const {
    organizations,
    currentOrganization,
    switchOrganization,
    getCurrentUserRole,
    canManageOrganization,
    loading
  } = useOrganization();

  const [anchorEl, setAnchorEl] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const open = Boolean(anchorEl);

  const isPersonalMode = currentOrganization?.is_personal === true;
  const personalOrgs = organizations.filter((o) => o.is_personal);
  const teamOrgs = organizations.filter((o) => !o.is_personal);

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleOrganizationSelect = (org) => {
    switchOrganization(org);
    handleClose();
  };

  const handleCreateNew = () => {
    setCreateDialogOpen(true);
    handleClose();
  };

  const handleSettings = () => {
    if (onSettingsClick) onSettingsClick();
    handleClose();
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'owner': return 'error';
      case 'admin': return 'warning';
      case 'member': return 'primary';
      default: return 'default';
    }
  };

  const getRoleLabel = (role) =>
    role ? role.charAt(0).toUpperCase() + role.slice(1) : '';

  if (loading && organizations.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 200 }}>
        <CircularProgress size={20} sx={{ mr: 1 }} />
        <Typography variant="body2" color="inherit">Loading...</Typography>
      </Box>
    );
  }

  if (!loading && organizations.length === 0) {
    return (
      <Box>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleCreateNew}
          size="small"
          data-tutorial-id="create-org-button"
        >
          Create Organization
        </Button>
        <CreateOrganizationForm
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
        />
      </Box>
    );
  }

  const displayLabel = isPersonalMode ? 'Personal Workspace' : (currentOrganization?.name || '');

  return (
    <Box>
      <Button
        onClick={handleClick}
        endIcon={<ArrowDownIcon />}
        data-tutorial-id="organization-switcher"
        sx={{ color: 'inherit', justifyContent: 'flex-start', minWidth: 200, maxWidth: 250 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
          {isPersonalMode ? (
            <Avatar sx={{ width: 24, height: 24, bgcolor: 'secondary.main' }}>
              <PersonIcon sx={{ fontSize: 14 }} />
            </Avatar>
          ) : (
            <Avatar
              src={currentOrganization?.avatar_url}
              sx={{ width: 24, height: 24, bgcolor: 'primary.main' }}
            >
              {!currentOrganization?.avatar_url && <BusinessIcon sx={{ fontSize: 14 }} />}
            </Avatar>
          )}
          <Box sx={{ overflow: 'hidden', textAlign: 'left' }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {displayLabel}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {isPersonalMode ? 'Personal workspace' : getRoleLabel(getCurrentUserRole())}
            </Typography>
          </Box>
        </Box>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{ sx: { minWidth: 280, maxWidth: 320 } }}
      >
        {/* Personal workspace section */}
        {personalOrgs.length > 0 && (
          <>
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="overline" color="text.secondary">Personal</Typography>
            </Box>
            {personalOrgs.map((org) => (
              <MenuItem
                key={org.id}
                onClick={() => handleOrganizationSelect(org)}
                selected={currentOrganization?.id === org.id}
                sx={{ py: 1.5 }}
              >
                <ListItemIcon>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                    <PersonIcon sx={{ fontSize: 16 }} />
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Personal Workspace
                    </Typography>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      {org.projects?.length ?? 0} project{(org.projects?.length ?? 0) !== 1 ? 's' : ''}
                    </Typography>
                  }
                />
              </MenuItem>
            ))}
            {teamOrgs.length > 0 && <Divider sx={{ my: 1 }} />}
          </>
        )}

        {/* Team organizations section */}
        {teamOrgs.length > 0 && (
          <>
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="overline" color="text.secondary">Organizations</Typography>
            </Box>
            {teamOrgs.map((org) => (
              <MenuItem
                key={org.id}
                onClick={() => handleOrganizationSelect(org)}
                selected={currentOrganization?.id === org.id}
                sx={{ py: 1.5 }}
              >
                <ListItemIcon>
                  <Avatar
                    src={org.avatar_url}
                    sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}
                  >
                    {!org.avatar_url && <BusinessIcon sx={{ fontSize: 16 }} />}
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {org.name}
                      </Typography>
                      {currentOrganization?.id === org.id && (
                        <Chip
                          label={getRoleLabel(getCurrentUserRole())}
                          size="small"
                          color={getRoleColor(getCurrentUserRole())}
                          variant="outlined"
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      {org.projects?.length ?? 0} project{(org.projects?.length ?? 0) !== 1 ? 's' : ''}
                    </Typography>
                  }
                />
              </MenuItem>
            ))}
          </>
        )}

        <Divider sx={{ my: 1 }} />

        <MenuItem onClick={handleCreateNew}>
          <ListItemIcon><AddIcon /></ListItemIcon>
          <ListItemText primary="Create Organization" />
        </MenuItem>

        {/* Hide org settings for personal workspace */}
        {canManageOrganization() && !isPersonalMode && (
          <MenuItem onClick={handleSettings}>
            <ListItemIcon><SettingsIcon /></ListItemIcon>
            <ListItemText primary="Organization Settings" />
          </MenuItem>
        )}
      </Menu>

      <CreateOrganizationForm
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
      />
    </Box>
  );
};

export default OrganizationSwitcher;
